import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Clock, Check, X, ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { API_BASE } from './lib/runtimeConfig';
import { useIsMobile } from './lib/responsive';
import WompiButton from './WompiButton';

const WHATSAPP_VYARA = '573052074956';
const manana = () => new Date(Date.now() - 5 * 3600000 + 86400000).toISOString().slice(0, 10);

const fmt = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const parseImages = (images) => {
    if (!images || images === '[]') return [];
    return images.replace(/[\[\]"']/g, '').split(',').map(s => s.trim()).filter(Boolean);
};

export default function ExperienciaPublica({ settings }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [exp, setExp] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [imgIdx, setImgIdx] = useState(0);
    const [lightbox, setLightbox] = useState(false);
    const [showBooking, setShowBooking] = useState(false);
    const [form, setForm] = useState({ guestName: '', guestEmail: '', guestPhone: '', fecha: '', numGuests: 1, notes: '' });
    const [legalOk, setLegalOk] = useState(false);
    const [aviso, setAviso] = useState('');
    const movil = useIsMobile(820);

    useEffect(() => {
        fetch(`${API_BASE}/api/experiences/slug/${slug}`)
            .then(r => { if (!r.ok) throw new Error('no encontrada'); return r.json(); })
            .then(data => { setExp(data); setLoading(false); document.title = `${data.title} | Vyara Group`; })
            .catch(err => { setError(err.message); setLoading(false); });
    }, [slug]);

    // Navegación con teclado dentro de la galería ampliada (← → para cambiar, Esc para cerrar)
    useEffect(() => {
        if (!lightbox) return;
        const onKey = (e) => {
            const images = parseImages(exp?.images);
            if (e.key === 'Escape') setLightbox(false);
            else if (e.key === 'ArrowLeft') setImgIdx(i => (i - 1 + images.length) % images.length);
            else if (e.key === 'ArrowRight') setImgIdx(i => (i + 1) % images.length);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [lightbox, exp]);

    // La reserva se crea en el servidor solo cuando Wompi aprueba el pago (el precio lo pone el servidor)
    const prepararPago = async () => {
        setAviso('');
        try {
            const res = await fetch(`${API_BASE}/api/wompi/experience-intent`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    experienceId: exp.id,
                    guestName: form.guestName,
                    guestEmail: form.guestEmail,
                    guestPhone: form.guestPhone,
                    activityDate: form.fecha,
                    numGuests: parseInt(form.numGuests) || 1,
                }),
            });
            const d = await res.json();
            if (!res.ok || !d?.reference) { setAviso(d?.error || 'No se pudo iniciar el pago. Inténtalo de nuevo.'); return null; }
            return { reference: d.reference, amount: d.amount };
        } catch { setAviso('Error de conexión. Inténtalo de nuevo.'); return null; }
    };

    if (loading) return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
            <div style={{ textAlign: 'center' }}>
                <div style={{ width: 48, height: 48, border: '4px solid #003366', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                <p style={{ color: '#64748b', fontFamily: 'Inter, sans-serif' }}>Cargando experiencia…</p>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );

    if (error || !exp) return (
        <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', fontFamily: 'Inter, sans-serif' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>🌊</div>
            <h2 style={{ color: '#1e293b', margin: '0 0 8px' }}>Experiencia no encontrada</h2>
            <p style={{ color: '#64748b', margin: '0 0 24px' }}>El enlace puede haber cambiado o la experiencia no está disponible.</p>
            <button onClick={() => navigate('/')} style={{ padding: '12px 28px', background: '#003366', color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer' }}>
                Ver todas las experiencias
            </button>
        </div>
    );

    const images = parseImages(exp.images);
    const cover = images[imgIdx] || 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80';
    const includes = (exp.includes || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    const notIncludes = (exp.notIncludes || '').split(/[,\n]/).map(s => s.trim()).filter(Boolean);
    const porSolicitud = exp.bookingMode === 'REQUEST';
    const total = (Number(exp.price) || 0) * (parseInt(form.numGuests) || 1);
    const listo = form.guestName.trim().length >= 3 && /\S+@\S+\.\S+/.test(form.guestEmail) && form.guestPhone.replace(/\D/g, '').length >= 7 && form.fecha >= manana() && legalOk;
    const enlaceWhatsapp = `https://wa.me/${(settings?.whatsappContact || WHATSAPP_VYARA).replace(/\D/g, '')}?text=${encodeURIComponent(`Hola, me interesa la experiencia: ${exp.title}`)}`;

    // En el celular la tarjeta de reserva va después del título (una sola columna); en el computador, a la derecha y fija
    const tarjeta = (
                <div style={{ background: '#fff', borderRadius: 20, padding: 28, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', ...(movil ? { padding: 20, margin: '0 0 24px' } : { position: 'sticky', top: 24 }) }}>
                    <div style={{ marginBottom: 20 }}>
                        <div style={{ fontSize: 28, fontWeight: 900, color: '#003366' }}>{fmt(exp.price)}</div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>por persona</div>
                    </div>

                    {porSolicitud ? (
                        <>
                            <p style={{ margin: '0 0 12px', fontSize: 13.5, color: '#475569', lineHeight: 1.5 }}>Esta experiencia tiene cupos limitados: primero confirmamos la disponibilidad para tu fecha y luego te enviamos el enlace de pago.</p>
                            <a href={enlaceWhatsapp} target="_blank" rel="noreferrer"
                                style={{ display: 'block', textAlign: 'center', padding: '14px', background: '#25D366', color: '#fff', borderRadius: 12, fontWeight: 800, fontSize: 15, textDecoration: 'none' }}>
                                💬 Consultar disponibilidad por WhatsApp
                            </a>
                        </>
                    ) : !showBooking ? (
                        <>
                            <button onClick={() => setShowBooking(true)}
                                style={{ width: '100%', padding: '14px', background: '#003366', color: '#fff', border: 'none', borderRadius: 12, fontWeight: 800, fontSize: 16, cursor: 'pointer' }}>
                                Reservar esta experiencia
                            </button>
                            <a href={enlaceWhatsapp} target="_blank" rel="noreferrer"
                                style={{ display: 'block', textAlign: 'center', marginTop: 10, padding: '13px', background: '#25D366', color: '#fff', borderRadius: 12, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
                                💬 Consultar por WhatsApp
                            </a>
                        </>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {[
                                { label: 'Nombre completo *', key: 'guestName', type: 'text', auto: 'name' },
                                { label: 'Correo *', key: 'guestEmail', type: 'email', auto: 'email' },
                                { label: 'Teléfono o WhatsApp *', key: 'guestPhone', type: 'tel', auto: 'tel' },
                                { label: 'Fecha de la actividad *', key: 'fecha', type: 'date', min: manana() },
                            ].map(f => (
                                <div key={f.key}>
                                    <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>{f.label}</label>
                                    <input type={f.type} min={f.min} autoComplete={f.auto} value={form[f.key]}
                                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                                        style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }} />
                                </div>
                            ))}
                            <div>
                                <label style={{ fontSize: 12, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>Número de personas</label>
                                <input type="number" min={1} max={30} value={form.numGuests}
                                    onChange={e => setForm(p => ({ ...p, numGuests: e.target.value }))}
                                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }} />
                            </div>
                            <div style={{ background: '#f0fdf4', borderRadius: 10, padding: '12px 16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: 14, color: '#374151' }}>Total a pagar</span>
                                <span style={{ fontWeight: 900, color: '#16a34a', fontSize: 18 }}>{fmt(total)}</span>
                            </div>
                            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12.5, color: '#475569', lineHeight: 1.5, cursor: 'pointer' }}>
                                <input type="checkbox" checked={legalOk} onChange={e => setLegalOk(e.target.checked)} style={{ width: 17, height: 17, marginTop: 2, flexShrink: 0 }} />
                                <span>Acepto los <strong>términos y condiciones</strong> y autorizo el tratamiento de mis datos personales.</span>
                            </label>
                            {aviso && <div style={{ background: '#fef2f2', color: '#b91c1c', borderRadius: 10, padding: '10px 12px', fontSize: 13, fontWeight: 600 }}>{aviso}</div>}
                            {!listo && <p style={{ margin: 0, fontSize: 12, color: '#94a3b8' }}>Completa tus datos, la fecha y acepta los términos para pagar.</p>}
                            <div style={{ opacity: listo ? 1 : 0.5 }}>
                                <WompiButton
                                    prepare={prepararPago}
                                    amount={total}
                                    redirectPath="/pago-confirmado"
                                    label="Pagar y reservar"
                                    customerEmail={form.guestEmail}
                                    customerName={form.guestName}
                                    customerPhone={form.guestPhone}
                                    disabled={!listo}
                                />
                            </div>
                            <p style={{ margin: 0, fontSize: 12, color: '#64748b', textAlign: 'center' }}>Tu cupo queda confirmado cuando se aprueba el pago. Te enviamos los detalles al correo.</p>
                            <button type="button" onClick={() => setShowBooking(false)}
                                style={{ padding: '10px', background: 'transparent', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 10, cursor: 'pointer', fontSize: 13 }}>
                                Cancelar
                            </button>
                        </div>
                    )}
                </div>
    );

    return (
        <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, sans-serif' }}>
            {/* Navbar mínima */}
            <div style={{ background: '#003366', padding: '16px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
                <button onClick={() => navigate(-1)} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 8, padding: '8px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, sans-serif' }}>
                    <ChevronLeft size={16} /> Volver
                </button>
                <span style={{ color: '#93c5fd', fontWeight: 700, fontSize: 13, marginLeft: 'auto' }}>VYARA GROUP</span>
            </div>

            <div style={{ maxWidth: 960, margin: '0 auto', padding: '32px 20px' }}>
                {/* Galería */}
                <div style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', height: 420, marginBottom: 32 }}>
                    <img src={cover} alt={exp.title} style={{ width: '100%', height: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
                        onClick={() => setLightbox(true)}
                        onError={e => { e.target.src = 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800&q=80'; }} />
                    <button onClick={() => setLightbox(true)} title="Ver galería completa"
                        style={{ position: 'absolute', top: 16, right: 16, background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Expand size={18} />
                    </button>
                    {images.length > 1 && (
                        <>
                            <button onClick={() => setImgIdx(i => (i - 1 + images.length) % images.length)}
                                style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ChevronLeft size={20} />
                            </button>
                            <button onClick={() => setImgIdx(i => (i + 1) % images.length)}
                                style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,0.5)', border: 'none', borderRadius: '50%', width: 40, height: 40, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <ChevronRight size={20} />
                            </button>
                            <div style={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 6 }}>
                                {images.map((_, i) => (
                                    <button key={i} onClick={() => setImgIdx(i)}
                                        style={{ width: i === imgIdx ? 20 : 8, height: 8, borderRadius: 4, background: i === imgIdx ? '#fff' : 'rgba(255,255,255,0.5)', border: 'none', cursor: 'pointer', transition: 'all 0.2s' }} />
                                ))}
                            </div>
                        </>
                    )}
                    <div style={{ position: 'absolute', top: 16, left: 16, background: '#003366', color: '#fff', padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                        {exp.category}
                    </div>
                </div>

                {/* Galería ampliada (lightbox) — clic en la foto principal para abrir */}
                {lightbox && (
                    <div onClick={() => setLightbox(false)}
                        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.9)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <button onClick={() => setLightbox(false)} title="Cerrar"
                            style={{ position: 'absolute', top: 20, right: 20, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 44, height: 44, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <X size={22} />
                        </button>
                        {images.length > 1 && (
                            <span style={{ position: 'absolute', top: 28, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 700, opacity: 0.8 }}>
                                {imgIdx + 1} / {images.length}
                            </span>
                        )}
                        <img src={cover} alt={exp.title} onClick={e => e.stopPropagation()}
                            style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }} />
                        {images.length > 1 && (
                            <>
                                <button onClick={e => { e.stopPropagation(); setImgIdx(i => (i - 1 + images.length) % images.length); }}
                                    style={{ position: 'absolute', left: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 48, height: 48, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ChevronLeft size={24} />
                                </button>
                                <button onClick={e => { e.stopPropagation(); setImgIdx(i => (i + 1) % images.length); }}
                                    style={{ position: 'absolute', right: 20, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 48, height: 48, color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <ChevronRight size={24} />
                                </button>
                            </>
                        )}
                    </div>
                )}

                <div style={{ display: 'grid', gridTemplateColumns: movil ? '1fr' : '1fr 340px', gap: movil ? 0 : 32, alignItems: 'start' }}>
                    {/* Info */}
                    <div>
                        <h1 style={{ margin: '0 0 12px', fontSize: movil ? 24 : 28, fontWeight: 900, color: '#1e293b' }}>{exp.title}</h1>
                        <div style={{ display: 'flex', gap: 20, marginBottom: 20, flexWrap: 'wrap' }}>
                            <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, color: '#64748b', fontSize: 14 }}><MapPin size={16} color="#003366" style={{ flexShrink: 0, marginTop: 2 }} /> <span><strong style={{ color: '#1e293b' }}>Punto de encuentro:</strong> {exp.location}</span></span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', fontSize: 14 }}><Clock size={16} color="#003366" /> <strong style={{ color: '#1e293b' }}>Duración:</strong> {/^\d+([.,]\d+)?$/.test(String(exp.duration || '').trim()) ? `${exp.duration} horas` : exp.duration}</span>
                        </div>
                        {movil && tarjeta}
                        <p style={{ color: '#475569', fontSize: 15, lineHeight: 1.7, margin: '0 0 28px', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>{exp.description}</p>

                        {includes.length > 0 && (
                            <div style={{ marginBottom: 24 }}>
                                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 800, color: '#1e293b' }}>Incluye</h3>
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {includes.map((item, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#374151' }}>
                                            <Check size={16} color="#16a34a" /> {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {notIncludes.length > 0 && (
                            <div>
                                <h3 style={{ margin: '0 0 12px', fontSize: 16, fontWeight: 800, color: '#1e293b' }}>No incluye</h3>
                                <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                                    {notIncludes.map((item, i) => (
                                        <li key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#64748b' }}>
                                            <X size={16} color="#ef4444" /> {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {!movil && tarjeta}
                </div>
            </div>
        </div>
    );
}
